import { useEffect, useRef, useState } from "react";
import { VISOR_MODULE_BREADCRUMB_ROOT } from "@/modules/workModuleCore";
import { useSearchParams, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePersistedClientId } from "@/lib/clientSelection";
import { PageHeader } from "./layoutChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Building2, Search, ShieldCheck, Upload, Loader2, FileText, FolderOpen, CheckCircle2, KeyRound, CloudDownload, PauseCircle, PlayCircle, Download, ListChecks } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import ClientCredentialsPanel from "@/components/clients/ClientCredentialsPanel";
import ClientCsfTabPanel from "@/components/clients/ClientCsfTabPanel";
import ClientFiscalObligationsTabPanel from "@/components/clients/ClientFiscalObligationsTabPanel";
import ClientSatSyncPanel from "@/components/clients/ClientSatSyncPanel";
import { ClientSatAutoSyncToggle, ClientFechaInicioDescargaField, ClientFechaInicioControlField } from "@/components/ClientForm";
import { todayIsoMx } from "@/lib/dateMx";
import { pickClientRepositoryRootPath, fetchRepositoryMode, generateOrVerifyLocalRepository } from "@/lib/clientRepositoryConfig";
import { inferTipoPersona, isPersonaMoral, shouldShowClientCurp } from "@/lib/clientLaboralProfile";
import { emptyContactosPm, contactosPmFromClient } from "@/lib/clientContactosPm";
import {
  defaultNombreComercialFromClient,
  displayClientNombre,
  mergeParsedClientFieldsIntoForm,
  mergeRazonSocialIntoForm,
  sanitizeRazonSocialForForm,
} from "@/lib/clientNombreComercial";
import ClientContactosSection from "@/components/clients/ClientContactosSection";
import FielCertificateAlertBadge from "@/components/FielCertificateAlertBadge";
import { useCertificateAlertsMap } from "@/components/CertificateExpirationBanner";
import { useCanShowClientSatAutoSyncConfig } from "@/lib/clientSatAutoSyncVisibility";
import { LaboralFlujoMaybe } from "@/components/laboral/LaboralFlujoOperativo";
import { IdentityValidateButton } from "@/components/identity/IdentityCheckDialog";
import {
  canCreateFiscalClients,
  canManageFiscalClientCatalog,
  downloadOperativoExport,
  formatOperativoDate,
  isClientOperativoSuspendido,
} from "@/lib/clientOperativoStatus";
import { despachoFiscalInfoApi } from "@/modules/despacho/despachoFiscalInfoApi";
import { isDespachoReadOnly } from "@/modules/despacho/despachoPermissions";
import { MODULO_DESPACHO_ID } from "@/modules/despacho/manifest";
import { useModuleAccess } from "@/modules/useModuleAccess";
import { useCapabilities } from "@/lib/useCapabilities";
import { parseClientesEditTab } from "@/lib/clientesDeepLink";
import { resolveDatosMaestrosAwareBreadcrumb } from "@/modules/datosMaestros/datosMaestrosNavigation";
import { useDatosMaestrosNavigationContext } from "@/modules/datosMaestros/useDatosMaestrosNavigationContext";
import {
  REGIMENES_SAT,
  normalizeRegimenFiscalForSelect,
  regimenSatSelectValue,
} from "@/lib/regimenesSat";

const empty = {
  rfc: "", razon_social: "", regimen_fiscal: "", codigo_postal: "", direccion: "", email: "", telefono: "", notas: "",
  modulo_descarga_activo: false,
  fecha_inicio_descarga: "",
  fecha_inicio_control: "",
  tipo_persona: "", representante_legal: "", cargo_representante: "", actividad_economica: "", municipio: "", estado: "",
  nombre_comercial: "",
  email_alterno: "",
  whatsapp_principal: "",
  whatsapp_alterno: "",
  contacto_principal: "",
  contacto_administrativo: "",
  contacto_contable: "",
  contacto_nomina: "",
  curp: "",
  canal_preferido: "",
  acepta_recordatorios: false,
  acepta_whatsapp: false,
  contactos_pm: emptyContactosPm(),
};

function toastSatSyncActivation(data, { editing = false } = {}) {
  const act = data?.sat_sync_activation;
  if (!act?.ui_message) return false;
  if (act.needs_fiel) {
    toast.info(act.ui_message);
    return true;
  }
  toast.success(act.ui_message);
  return true;
}

export default function Clientes() {
  const { user } = useAuth();
  const location = useLocation();
  useDatosMaestrosNavigationContext();
  const { hasModuleAccess: hasDespachoModule } = useModuleAccess(MODULO_DESPACHO_ID);
  const { permissions, role, isPlatformDesigner, loading: capabilitiesLoading } = useCapabilities();
  const despachoFiscalReadOnly = isDespachoReadOnly({
    permissions,
    role,
    isPlatformDesigner,
    capabilitiesLoading,
    user,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const canManageOperativo = canManageFiscalClientCatalog(user);
  const canShowSatAutoSync = useCanShowClientSatAutoSyncConfig();
  const { alertsByClientId } = useCertificateAlertsMap();
  const rawPageTab =
    tabParam === "sincronizacion"
      ? "sincronizacion"
      : tabParam === "suspendidos"
        ? "suspendidos"
        : "catalogo";
  const pageTab =
    (rawPageTab === "sincronizacion" && !canShowSatAutoSync) || (rawPageTab === "suspendidos" && !canManageOperativo)
      ? "catalogo"
      : rawPageTab;
  const isAdmin = Boolean(user?.has_global_client_supervision);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [dialogTab, setDialogTab] = useState("manual");
  const [editDialogTab, setEditDialogTab] = useState("datos");
  /** Alta por acordeón (startNew): sin salto de pestaña a credenciales. */
  const [clientAltaMode, setClientAltaMode] = useState(false);
  const [altaAccordionOpen, setAltaAccordionOpen] = useState(["datos"]);
  const [csfBusy, setCsfBusy] = useState(false);
  const [csfWarnings, setCsfWarnings] = useState([]);
  const [taxRepoPath, setTaxRepoPath] = useState("");
  const [taxRepoBusy, setTaxRepoBusy] = useState(false);
  const [repoEnsureBusyId, setRepoEnsureBusyId] = useState("");
  const [repoServerManaged, setRepoServerManaged] = useState(false);
  const [clientId, setClientId] = usePersistedClientId(clients);

  const canCreate = canCreateFiscalClients(user);
  const canDelete = canManageFiscalClientCatalog(user);
  const showFiscalObligationsTab = Boolean(hasDespachoModule);
  // Datos + Constancia + Credenciales (+ Obligaciones si aplica). Materialidad vive solo en su módulo.
  const editDialogTabCount = editing?.id
    ? 3 + (showFiscalObligationsTab ? 1 : 0)
    : 2;
  const editDialogTabColsClass =
    editDialogTabCount >= 5
      ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
      : editDialogTabCount >= 4
        ? "grid-cols-2 sm:grid-cols-4"
        : editDialogTabCount === 3
          ? "grid-cols-3"
          : "grid-cols-2";
  const clientDialogWide = Boolean(
    editing?.id && editDialogTab === "obligaciones",
  );
  const [operativoBusyId, setOperativoBusyId] = useState("");
  const [exportBusy, setExportBusy] = useState("");
  const pendingCsfFileRef = useRef(null);

  const clientsTabCount = 1 + (canShowSatAutoSync ? 1 : 0) + (canManageOperativo ? 1 : 0);
  const clientsTabColsClass =
    clientsTabCount >= 4
      ? "grid-cols-2 sm:grid-cols-4"
      : clientsTabCount === 3
        ? "grid-cols-2 sm:grid-cols-3"
        : clientsTabCount === 2
          ? "grid-cols-2"
          : "grid-cols-1";

  const selectWorkClient = (c) => {
    setClientId(c.id);
    toast.success(`Cliente de trabajo: ${c.razon_social || c.rfc}`);
    if (pageTab === "sincronizacion") {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", pageTab);
        next.set("client", c.id);
        return next;
      }, { replace: true });
    }
  };

  const load = () =>
    api.get("/clients")
      .then(({ data }) => setClients(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error(err);
        setClients([]);
        toast.error("Error al cargar clientes");
      });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const qClient = searchParams.get("client");
    if (qClient && clients.some((c) => c.id === qClient)) {
      setClientId(qClient);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, clients.length]);
  useEffect(() => {
    fetchRepositoryMode()
      .then((data) => setRepoServerManaged(Boolean(data?.server_managed)))
      .catch(() => setRepoServerManaged(false));
  }, []);
  useEffect(() => {
    const onReset = () => load();
    window.addEventListener("visor:operational-data-reset", onReset);
    return () => window.removeEventListener("visor:operational-data-reset", onReset);
  }, []);

  const persistClient = async ({ openCredentialsAfterCreate = false } = {}) => {
    const rfc = (form.rfc || "").trim();
    const razon = (form.razon_social || "").trim();
    if (!editing?.id) {
      if (!rfc || !razon) {
        toast.error("Indique RFC y razón social en Datos del cliente antes de cargar la FIEL");
        setEditDialogTab("datos");
        return false;
      }
    }
    if (canShowSatAutoSync && form.modulo_descarga_activo && !(form.fecha_inicio_descarga || "").trim()) {
      toast.error("Indique la fecha inicio de descarga histórica para la sincronización automática");
      setEditDialogTab("datos");
      return false;
    }
    const payload = { ...form };
    payload.regimen_fiscal = normalizeRegimenFiscalForSelect(payload.regimen_fiscal) || payload.regimen_fiscal || "";
    if (payload.modulo_descarga_activo && !payload.fecha_inicio_descarga) {
      payload.fecha_inicio_descarga = todayIsoMx();
    }
    setBusy(true);
    try {
      if (editing?.id) {
        const { data: upd } = await api.put(`/clients/${editing.id}`, payload);
        if (!toastSatSyncActivation(upd, { editing: true })) {
          toast.success("Cliente actualizado");
        }
        setOpen(false);
        setEditing(null);
        setForm(empty);
        setTaxRepoPath("");
        load();
        return true;
      }
      const body = { ...payload };
      if (!repoServerManaged && taxRepoPath.trim()) {
        body.tax_repository_root_path = taxRepoPath.trim();
      }
      const { data } = await api.post("/clients", body);
      if (data?.repository_warning) {
        toast.warning(
          `El cliente se registró en la nube, pero la carpeta local no se creó: ${data.repository_warning}`,
        );
      }
      const csfFile = pendingCsfFileRef.current;
      pendingCsfFileRef.current = null;
      if (data?.id && csfFile) {
        try {
          const uploadResult = await despachoFiscalInfoApi.uploadCsf(data.id, csfFile);
          const added = uploadResult?.suggested_added ?? 0;
          if (added > 0) {
            toast.success(`${added} obligación(es) previa(s) activada(s) desde la CFS.`);
          }
        } catch (uploadErr) {
          console.warn("No se pudo almacenar la CFS tras crear el cliente", uploadErr);
        }
      }
      const created = { ...form, ...data };
      setEditing(created);
      if (clientAltaMode) {
        setAltaAccordionOpen(["credenciales"]);
      } else {
        setEditDialogTab("credenciales");
      }
      if (data?.id) {
        setClientId(data.id);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          next.set("client", data.id);
          return next;
        }, { replace: true });
      }
      if (!toastSatSyncActivation(data)) {
        if (clientAltaMode) {
          toast.success(
            openCredentialsAfterCreate || form.modulo_descarga_activo
              ? "Empresa creada. Cargue la e.firma en la sección 2 del mismo diálogo."
              : "Empresa creada. Puede cargar certificados en la sección 2 o cerrar y hacerlo después.",
          );
        } else if (openCredentialsAfterCreate || form.modulo_descarga_activo) {
          toast.success("Cliente creado. Guarde la e.firma (FIEL) en el diálogo para continuar.");
        } else {
          toast.success("Cliente creado. Cargue e.firma y registros patronales en el diálogo.");
        }
      }
      setQ("");
      load();
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "CLIENT_RFC_EXISTS") {
        const rfcHit = (detail.rfc || form.rfc || "").toString().trim().toUpperCase();
        const label = (detail.razon_social || rfcHit || "el RFC indicado").toString();
        setQ(rfcHit || label);
        await load();
        if (detail.reassigned) {
          toast.message(
            `El RFC ya estaba registrado (${label}). Se asignó a su cartera; ya debe aparecer en el catálogo.`,
          );
        } else {
          toast.message(
            `Ya existe un cliente con RFC ${rfcHit || "indicado"} (${label}). Use la búsqueda del catálogo para abrirlo.`,
          );
        }
        const existingId = detail.client_id;
        if (existingId) {
          // Tras load, intentar abrir edición del existente.
          try {
            const { data: list } = await api.get("/clients");
            const rows = Array.isArray(list) ? list : [];
            setClients(rows);
            const hit = rows.find((c) => c.id === existingId || (c.rfc || "").toUpperCase() === rfcHit);
            if (hit) {
              startEdit(hit);
            }
          } catch {
            /* listado ya pedido arriba */
          }
        }
        return false;
      }
      toast.error(formatApiError(detail) || "Error al guardar");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    await persistClient();
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar cliente ${c.razon_social}? Se eliminarán también sus CFDI y Excel.`)) return;
    await api.delete(`/clients/${c.id}`);
    toast.success("Cliente eliminado");
    load();
  };

  const setOperativoStatus = async (c, estatus) => {
    if (!c?.id) return;
    const suspendiendo = estatus === "suspendido";
    const motivo = suspendiendo
      ? window.prompt("Motivo de suspensión (opcional):", "") || ""
      : "";
    if (
      suspendiendo &&
      !window.confirm(
        `¿Suspender operación de «${c.razon_social}»?\n\nSe detiene SAT automático, asignaciones y operación del despacho. Los datos fiscales se conservan.`,
      )
    ) {
      return;
    }
    if (
      !suspendiendo &&
      !window.confirm(`¿Reanudar operación de «${c.razon_social}»? La sincronización SAT no se activa sola.`)
    ) {
      return;
    }
    setOperativoBusyId(c.id);
    try {
      const { data } = await api.patch(`/clients/${c.id}/operativo-status`, {
        estatus,
        motivo,
      });
      toast.success(suspendiendo ? "Cliente suspendido." : "Cliente reanudado.");
      setClients((prev) => prev.map((row) => (row.id === c.id ? { ...row, ...data } : row)));
      if (clientId === c.id && suspendiendo) {
        setClientId("");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo actualizar el estatus operativo.");
    } finally {
      setOperativoBusyId("");
    }
  };

  const handleOperativoExport = async (estatus, format) => {
    const key = `${estatus}-${format}`;
    setExportBusy(key);
    try {
      await downloadOperativoExport({ estatus, format });
      toast.success("Listado descargado.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo exportar el listado.");
    } finally {
      setExportBusy("");
    }
  };

  const startEdit = (c, { tab = "datos" } = {}) => {
    pendingCsfFileRef.current = null;
    setClientAltaMode(false);
    setEditing(c);
    const resolvedTab =
      tab === "obligaciones" && hasDespachoModule === false ? "constancia" : tab;
    setEditDialogTab(resolvedTab);
    const regimenNorm = normalizeRegimenFiscalForSelect(c.regimen_fiscal);
    const tipo = inferTipoPersona({ ...c, regimen_fiscal: regimenNorm || c.regimen_fiscal });
    setForm({
      ...empty,
      ...c,
      regimen_fiscal: regimenNorm,
      razon_social: sanitizeRazonSocialForForm(c.razon_social),
      nombre_comercial: defaultNombreComercialFromClient(c),
      modulo_descarga_activo: Boolean(c.modulo_descarga_activo),
      fecha_inicio_descarga: (c.fecha_inicio_descarga || "").slice(0, 10),
      fecha_inicio_control: (c.fecha_inicio_control || "").slice(0, 10),
      tipo_persona: tipo,
      curp: (c.curp || "").trim().toUpperCase(),
      acepta_recordatorios: Boolean(c.acepta_recordatorios),
      acepta_whatsapp: Boolean(c.acepta_whatsapp),
      contactos_pm: contactosPmFromClient(c),
    });
    setDialogTab("manual");
    setCsfWarnings([]);
    setOpen(true);
    // Si PF sin CURP pero hay CFS, rellenar desde la constancia guardada.
    if (c?.id && tipo === "fisica" && !(c.curp || "").trim()) {
      api
        .post(`/clients/${c.id}/hydrate-from-csf`)
        .then(({ data }) => {
          const curp = (data?.patched?.curp || data?.client?.curp || "").trim().toUpperCase();
          if (!curp) return;
          setForm((prev) => ({
            ...prev,
            ...(data?.patched || {}),
            curp,
            tipo_persona: "fisica",
            regimen_fiscal: normalizeRegimenFiscalForSelect(
              data?.patched?.regimen_fiscal ?? data?.client?.regimen_fiscal ?? prev.regimen_fiscal,
            ),
          }));
          setEditing((prev) => (prev?.id === c.id ? { ...prev, ...(data?.client || {}), curp } : prev));
          setClients((prev) =>
            prev.map((row) => (row.id === c.id ? { ...row, ...(data?.client || {}), curp } : row)),
          );
        })
        .catch(() => {});
    }
    if (c?.id && c.modulo_descarga_activo) {
      api
        .post(`/clients/${c.id}/sat-sync-reconcile`)
        .then(({ data }) => {
          if (!data) return;
          setForm((prev) => {
            const next = {
              ...prev,
              ...data,
              regimen_fiscal: normalizeRegimenFiscalForSelect(
                data.regimen_fiscal ?? prev.regimen_fiscal,
              ),
            };
            // No pisar CURP del formulario con vacío del reconcile (carrera con CFS).
            const prevCurp = String(prev.curp || "").trim();
            const dataCurp = String(data.curp || "").trim();
            if (prevCurp && !dataCurp) {
              next.curp = prevCurp;
            }
            if (prev.tipo_persona === "fisica" && data.tipo_persona === "moral") {
              const rfcLen = String(next.rfc || prev.rfc || "").replace(/\s/g, "").length;
              if (rfcLen === 13) next.tipo_persona = "fisica";
            }
            return next;
          });
          setEditing((prev) => {
            if (prev?.id !== c.id) return prev;
            const merged = { ...prev, ...data };
            if (String(prev.curp || "").trim() && !String(data.curp || "").trim()) {
              merged.curp = prev.curp;
            }
            return merged;
          });
        })
        .catch(() => {});
    }
  };
  const startNew = (tab = "manual") => {
    setEditing(null);
    setClientAltaMode(true);
    setAltaAccordionOpen(["datos"]);
    setEditDialogTab("datos");
    setForm(empty);
    setTaxRepoPath("");
    pendingCsfFileRef.current = null;
    setDialogTab(tab);
    setCsfWarnings([]);
    setOpen(true);
  };

  useEffect(() => {
    const deepTab = parseClientesEditTab(tabParam);
    if (!deepTab) return;
    if (deepTab === "obligaciones" && hasDespachoModule == null) return;
    const qClient = searchParams.get("client");
    if (!qClient) return;
    if (!clients.length) return;
    const target = clients.find((c) => c.id === qClient);
    if (!target) {
      toast.error(
        "No se encontró esa empresa en su catálogo. Si es auxiliar, confirme que esté asignada a usted.",
      );
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      }, { replace: true });
      return;
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("tab");
      if (qClient) next.set("client", qClient);
      return next;
    }, { replace: true });
    startEdit(target, { tab: deepTab });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam, clients.length, hasDespachoModule]);

  const onPickTaxRepository = async () => {
    setTaxRepoBusy(true);
    try {
      const path = await pickClientRepositoryRootPath();
      setTaxRepoPath(path);
    } catch (e) {
      if (e?.message) toast.error(e.message);
    } finally {
      setTaxRepoBusy(false);
    }
  };

  const onEnsureLocalRepository = async (client) => {
    if (repoServerManaged) {
      toast.info("En este despliegue el repositorio se gestiona en el servidor; no requiere carpeta en su PC.");
      return;
    }
    if (!client?.id) return;
    setRepoEnsureBusyId(client.id);
    try {
      const saved = await generateOrVerifyLocalRepository(client.id);
      toast.success(
        saved?.message ||
          `Repositorio listo en ${saved?.ruta_repositorio_local || saved?.root_path || "disco local"}`,
      );
    } catch (e) {
      toast.error(e?.message || formatApiError(e.response?.data?.detail) || "No se pudo generar el repositorio");
    } finally {
      setRepoEnsureBusyId("");
    }
  };

  const onCsfPdfSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    pendingCsfFileRef.current = file;
    setCsfBusy(true);
    setCsfWarnings([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/clients/import-csf-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const { parse_warnings, text_preview, ok, ...fields } = data;
      const regimen = normalizeRegimenFiscalForSelect(fields.regimen_fiscal) || fields.regimen_fiscal;
      setForm((f) => {
        const merged = mergeParsedClientFieldsIntoForm(f, {
          ...fields,
          regimen_fiscal: regimen || fields.regimen_fiscal,
        });
        merged.tipo_persona = inferTipoPersona(merged);
        if (!isPersonaMoral(merged)) {
          merged.representante_legal = "";
          merged.cargo_representante = "";
          merged.contactos_pm = emptyContactosPm();
        } else if (String(merged.rfc || "").replace(/\s/g, "").length === 12) {
          merged.curp = "";
        }
        return merged;
      });
      setCsfWarnings(Array.isArray(parse_warnings) ? parse_warnings : []);
      setDialogTab("manual");
      toast.success("Datos leídos del PDF. Revise y pulse Crear.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo leer el PDF");
    } finally {
      setCsfBusy(false);
    }
  };

  const onApplyCsfParsedToForm = (parsed) => {
    if (!parsed || typeof parsed !== "object") return;
    const regimen = normalizeRegimenFiscalForSelect(parsed.regimen_fiscal) || parsed.regimen_fiscal;
    let mergedSnapshot = null;
    setForm((f) => {
      const merged = mergeParsedClientFieldsIntoForm(f, {
        ...parsed,
        regimen_fiscal: regimen || parsed.regimen_fiscal,
      });
      merged.tipo_persona = inferTipoPersona(merged);
      if (!isPersonaMoral(merged)) {
        merged.representante_legal = "";
        merged.cargo_representante = "";
        merged.contactos_pm = emptyContactosPm();
      } else if (String(merged.rfc || "").replace(/\s/g, "").length === 12) {
        merged.curp = "";
      }
      mergedSnapshot = merged;
      return merged;
    });
    // Reflejar en el listado/edición los campos que vienen de la CSF.
    if (editing?.id) {
      const patch = {
        regimen_fiscal: regimen || parsed.regimen_fiscal || undefined,
        codigo_postal: (parsed.codigo_postal || "").trim() || undefined,
        direccion: (parsed.direccion || "").trim() || undefined,
        rfc: (parsed.rfc || "").trim() || undefined,
        razon_social: (parsed.razon_social || "").trim() || undefined,
        curp: (parsed.curp || "").trim().toUpperCase() || undefined,
        tipo_persona: inferTipoPersona({
          ...editing,
          ...parsed,
          regimen_fiscal: regimen || parsed.regimen_fiscal || editing?.regimen_fiscal,
        }),
      };
      const clean = Object.fromEntries(Object.entries(patch).filter(([, v]) => v));
      if (Object.keys(clean).length) {
        setEditing((prev) => (prev ? { ...prev, ...clean } : prev));
        setClients((prev) =>
          prev.map((c) => (c.id === editing.id ? { ...c, ...clean } : c)),
        );
      }
      // Persistir CURP/identidad de inmediato (sin cerrar el diálogo) para que no se pierda.
      const payloadBase = mergedSnapshot || { ...form, ...clean };
      const payload = {
        ...payloadBase,
        ...clean,
        regimen_fiscal:
          normalizeRegimenFiscalForSelect(payloadBase.regimen_fiscal || clean.regimen_fiscal)
          || payloadBase.regimen_fiscal
          || "",
      };
      api
        .put(`/clients/${editing.id}`, payload)
        .then(({ data: upd }) => {
          const curp = (upd?.curp || clean.curp || "").trim().toUpperCase();
          if (curp) {
            setForm((prev) => ({ ...prev, curp, tipo_persona: upd?.tipo_persona || prev.tipo_persona || "fisica" }));
          }
          setEditing((prev) => (prev?.id === editing.id ? { ...prev, ...upd, curp: curp || prev.curp } : prev));
          setClients((prev) =>
            prev.map((c) => (c.id === editing.id ? { ...c, ...upd, curp: curp || c.curp } : c)),
          );
        })
        .catch((err) => {
          console.warn("No se pudo autoguardar CURP tras CFS", err);
        });
    }
    setEditDialogTab("datos");
  };

  const onPageTabChange = (tab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === "sincronizacion") {
        next.set("tab", tab);
        if (clientId) next.set("client", clientId);
      } else if (tab === "suspendidos") {
        next.set("tab", "suspendidos");
        next.delete("client");
      } else {
        next.delete("tab");
        next.delete("client");
      }
      return next;
    }, { replace: true });
  };

  const onSyncClientUpdated = (doc) => {
    if (!doc?.id) {
      load();
      return;
    }
    setClients((prev) => prev.map((c) => (c.id === doc.id ? { ...c, ...doc } : c)));
    if (clientId === doc.id) {
      setForm((prev) => ({ ...prev, ...doc }));
    }
    if (editing?.id === doc.id) {
      setEditing((prev) => (prev ? { ...prev, ...doc } : prev));
    }
  };

  const filtered = clients.filter((c) => {
    if (pageTab === "suspendidos") {
      if (!isClientOperativoSuspendido(c)) return false;
    } else if (pageTab === "catalogo") {
      if (isClientOperativoSuspendido(c)) return false;
    }
    const matchesQ =
      !q ||
      c.razon_social.toLowerCase().includes(q.toLowerCase()) ||
      c.rfc.toLowerCase().includes(q.toLowerCase()) ||
      (isAdmin && (c.owner_email || "").toLowerCase().includes(q.toLowerCase()));
    return matchesQ;
  });

  const vigentesCount = clients.filter((c) => !isClientOperativoSuspendido(c)).length;
  const suspendidosCount = clients.filter((c) => isClientOperativoSuspendido(c)).length;

  return (
    <>
            <Alert className="rounded-sm border-sky-200 bg-sky-50/80 mb-4" data-testid="clientes-fiscal-domain-notice">
        <AlertTitle className="text-sm text-sky-950">Clientes fiscales / contribuyentes</AlertTitle>
        <AlertDescription className="text-xs text-sky-900">
          Empresas o personas que administra su despacho para SAT, CFDI, laboral y trámites. Distinto del{" "}
          <strong>comprador BNT</strong> (licencia SaaS), que solo se administra en el módulo Diseñador / Centro SaaS.
        </AlertDescription>
      </Alert>

      <PageHeader
        title="Clientes"
        subtitle={isAdmin ? "Vista de administrador: todos los clientes de todos los contadores del despacho." : "Catálogo de contribuyentes y datos fiscales."}
        breadcrumb={resolveDatosMaestrosAwareBreadcrumb(location, VISOR_MODULE_BREADCRUMB_ROOT, "Clientes")}
        right={
          <div className="flex items-center gap-2">
            {isAdmin && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[#3DA873]/10 text-[#3DA873] rounded-sm text-xs font-semibold" data-testid="admin-badge">
                <ShieldCheck className="w-3.5 h-3.5" />
                Supervisión Admin
              </div>
            )}
            {canCreate ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => startNew("csf")}
                  className="rounded-sm h-9 border-[#0F2D5C] text-[#0F2D5C]"
                  data-testid="new-client-csf-btn"
                >
                  <FileText className="w-4 h-4 mr-1.5" /> Alta desde CSF (PDF)
                </Button>
                <Button onClick={() => startNew("manual")} className="bg-slate-900 hover:bg-slate-800 rounded-sm h-9" data-testid="new-client-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Nuevo cliente
                </Button>
              </>
            ) : null}
          </div>
        }
      />
      <LaboralFlujoMaybe>
      <div className="p-8 space-y-4">
        <Tabs value={pageTab} onValueChange={onPageTabChange} className="w-full">
          <TabsList
            className={`grid w-full max-w-3xl h-auto sm:h-9 rounded-sm mb-4 gap-1 ${clientsTabColsClass}`}
          >
            <TabsTrigger value="catalogo" className="text-xs rounded-sm" data-testid="clients-tab-catalogo">
              Catálogo ({vigentesCount})
            </TabsTrigger>
            {canShowSatAutoSync ? (
            <TabsTrigger value="sincronizacion" className="text-xs rounded-sm" data-testid="clients-tab-sincronizacion">
              <CloudDownload className="w-3.5 h-3.5 mr-1.5" />
              Sincronización SAT
            </TabsTrigger>
            ) : null}
            {canManageOperativo ? (
              <TabsTrigger value="suspendidos" className="text-xs rounded-sm" data-testid="clients-tab-suspendidos">
                <PauseCircle className="w-3.5 h-3.5 mr-1.5" />
                Suspendidos ({suspendidosCount})
              </TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent value="catalogo" className="mt-0 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input
              data-testid="clients-search-input"
              placeholder={isAdmin ? "Buscar por razón social, RFC o correo del contador (dueño)…" : "Buscar por razón social o RFC…"}
              value={q} onChange={(e)=>setQ(e.target.value)}
              className="pl-9 rounded-sm"
            />
          </div>
          {canManageOperativo ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-sm h-8"
                disabled={exportBusy === "activo-csv"}
                onClick={() => handleOperativoExport("activo", "csv")}
                data-testid="clients-export-activos-csv"
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                Excel vigentes
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-sm h-8"
                disabled={exportBusy === "activo-pdf"}
                onClick={() => handleOperativoExport("activo", "pdf")}
                data-testid="clients-export-activos-pdf"
              >
                PDF vigentes
              </Button>
            </div>
          ) : null}
          {!repoServerManaged && filtered.length > 0 ? (
            <p className="text-[11px] text-slate-500 max-w-xl">
              Los CFDI del SAT se guardan en la nube (MongoDB). Use{" "}
              <strong>Generar / Verificar Repositorio Local</strong> solo para exportación a disco.
            </p>
          ) : null}
        </div>

        <div
          className="bg-white border border-slate-200 rounded-sm overflow-x-auto"
          data-testid="clients-table"
        >
          <table className="w-full text-sm min-w-[960px]">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">RFC</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Razón social</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Régimen</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">CP</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Correo</th>
                {isAdmin && <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Contador (dueño)</th>}
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Trabajo</th>
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold sticky right-0 bg-slate-50 z-10 min-w-[200px] shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 8 : 7} className="px-4 py-12 text-center">
                  <Building2 className="w-8 h-8 mx-auto text-slate-300 mb-2"/>
                  <div className="text-sm text-slate-500">{q ? "Sin resultados." : "Aún no hay clientes registrados."}</div>
                </td></tr>
              )}
              {filtered.map(c => {
                const isActiveWork = c.id === clientId;
                return (
                <tr
                  key={c.id}
                  className={[
                    "border-t border-slate-100 row-hover",
                    isActiveWork ? "bg-emerald-50/50" : "",
                  ].join(" ")}
                  data-testid={`client-row-${c.rfc}`}
                >
                  <td className="px-4 py-2.5 font-mono text-slate-900">{c.rfc}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-900">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={!displayClientNombre(c) ? "text-amber-700" : undefined}>
                        {displayClientNombre(c) || "Sin nombre — reaplicar CSF"}
                      </span>
                      {alertsByClientId[c.id] ? (
                        <FielCertificateAlertBadge
                          light={alertsByClientId[c.id].traffic_light}
                          days={alertsByClientId[c.id].days_remaining}
                          compact
                          label={
                            alertsByClientId[c.id].cert_type === "FIEL representante"
                              ? "Rep. FIEL"
                              : "FIEL"
                          }
                        />
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{c.regimen_fiscal || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-slate-600">{c.codigo_postal || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{c.email || "—"}</td>
                  {isAdmin && (
                    <td className="px-4 py-2.5 text-slate-600 text-xs font-mono" data-testid={`client-owner-${c.rfc}`}>
                      {c.owner_email || <span className="text-slate-400">—</span>}
                    </td>
                  )}
                  <td className="px-4 py-2.5">
                    {isActiveWork ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#3DA873]">
                        <CheckCircle2 className="w-3.5 h-3.5" aria-hidden />
                        Activo
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => selectWorkClient(c)}
                        className="h-8 rounded-sm text-xs border-[#0F2D5C]/30 text-[#0F2D5C]"
                        data-testid={`select-work-client-${c.rfc}`}
                      >
                        Usar
                      </Button>
                    )}
                  </td>
                  <td
                    className={[
                      "px-3 py-2.5 text-right sticky right-0 z-10 min-w-[200px]",
                      "shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]",
                      isActiveWork ? "bg-emerald-50/95" : "bg-white",
                    ].join(" ")}
                  >
                    <div className="flex flex-wrap items-center justify-end gap-1">
                      {!repoServerManaged ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={repoEnsureBusyId === c.id}
                          onClick={() => onEnsureLocalRepository(c)}
                          className="h-8 rounded-sm text-[#0F2D5C] border-[#0F2D5C]/30 text-xs hidden lg:inline-flex"
                          data-testid={`ensure-repo-${c.rfc}`}
                          title="Generar repositorio local"
                        >
                          {repoEnsureBusyId === c.id ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                          ) : (
                            <FolderOpen className="w-3.5 h-3.5 mr-1" />
                          )}
                          Repo
                        </Button>
                      ) : null}
                      <Button
                        data-testid={`edit-client-${c.rfc}`}
                        size="sm"
                        variant="outline"
                        onClick={() => startEdit(c)}
                        className="h-8 rounded-sm border-slate-300"
                        title="Editar cliente"
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1" />
                        <span className="text-xs">Editar</span>
                      </Button>
                      {canManageOperativo ? (
                        <Button
                          data-testid={`suspend-client-${c.rfc}`}
                          size="sm"
                          variant="outline"
                          disabled={operativoBusyId === c.id}
                          onClick={() => setOperativoStatus(c, "suspendido")}
                          className="h-8 rounded-sm border-amber-200 text-amber-900 hover:bg-amber-50"
                          title="Suspender operación del despacho"
                        >
                          <PauseCircle className="w-3.5 h-3.5 mr-1" />
                          <span className="text-xs">Suspender</span>
                        </Button>
                      ) : null}
                      {canDelete ? (
                      <Button
                        data-testid={`delete-client-${c.rfc}`}
                        size="sm"
                        variant="outline"
                        onClick={() => remove(c)}
                        className="h-8 rounded-sm border-red-200 text-red-700 hover:bg-red-50"
                        title="Eliminar cliente"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        <span className="text-xs">Eliminar</span>
                      </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
          </TabsContent>

          {canManageOperativo ? (
            <TabsContent value="suspendidos" className="mt-0 space-y-4" data-testid="clients-suspendidos-panel">
              <p className="text-xs text-slate-600 m-0 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2">
                Empresas con operación congelada: sin SAT automático, sin asignación a auxiliares ni acciones operativas.
                Los CFDI y credenciales se conservan hasta que reanude o elimine el cliente.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-8"
                  disabled={exportBusy === "suspendido-csv"}
                  onClick={() => handleOperativoExport("suspendido", "csv")}
                  data-testid="clients-export-suspendidos-csv"
                >
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Excel suspendidos
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-8"
                  disabled={exportBusy === "suspendido-pdf"}
                  onClick={() => handleOperativoExport("suspendido", "pdf")}
                  data-testid="clients-export-suspendidos-pdf"
                >
                  PDF suspendidos
                </Button>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="clients-suspendidos-table">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">RFC</th>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Razón social</th>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Inicio</th>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Suspendido</th>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Motivo</th>
                      <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">
                          No hay clientes suspendidos.
                        </td>
                      </tr>
                    ) : (
                      filtered.map((c) => (
                        <tr key={c.id} className="border-t border-slate-100" data-testid={`client-suspendido-row-${c.rfc}`}>
                          <td className="px-4 py-2.5 font-mono">{c.rfc}</td>
                          <td className="px-4 py-2.5 font-medium">
                            {displayClientNombre(c) || "Sin nombre — reaplicar CSF"}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-slate-600">{formatOperativoDate(c.operativo_desde || c.created_at)}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-600">{formatOperativoDate(c.suspended_at)}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-600">{c.suspension_motivo || "—"}</td>
                          <td className="px-4 py-2.5 text-right">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 rounded-sm border-emerald-200 text-emerald-900 hover:bg-emerald-50"
                              disabled={operativoBusyId === c.id}
                              onClick={() => setOperativoStatus(c, "activo")}
                              data-testid={`resume-client-${c.rfc}`}
                            >
                              <PlayCircle className="w-3.5 h-3.5 mr-1" />
                              Reanudar
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          ) : null}

          {canShowSatAutoSync ? (
          <TabsContent value="sincronizacion" className="mt-0">
            <ClientSatSyncPanel
              clientId={clientId}
              clients={clients}
              onSelectClientId={setClientId}
              onClientUpdated={onSyncClientUpdated}
            />
          </TabsContent>
          ) : null}
        </Tabs>
      </div>
      </LaboralFlujoMaybe>

      <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){setEditing(null); setForm(empty); setCsfWarnings([]); setDialogTab("manual"); setEditDialogTab("datos"); setTaxRepoPath(""); setClientAltaMode(false); setAltaAccordionOpen(["datos"]);}}}>
        <DialogContent className={`${clientDialogWide ? "max-w-6xl" : editing?.id || clientAltaMode ? "max-w-4xl" : "max-w-2xl"} rounded-sm max-h-[90vh] overflow-y-auto`} data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle style={{fontFamily:"'Chivo'"}}>
              {clientAltaMode
                ? editing?.id
                  ? "Nuevo cliente — certificados (opcional)"
                  : "Nuevo cliente"
                : editing?.id && editDialogTab === "credenciales"
                ? "Certificados y FIEL"
                : editing?.id && editDialogTab === "constancia"
                  ? "Constancia fiscal"
                  : editing?.id && editDialogTab === "obligaciones"
                    ? "Obligaciones fiscales"
                  : editing?.id
                    ? "Editar cliente"
                    : "Nuevo cliente"}
            </DialogTitle>
          </DialogHeader>
          <Tabs value={clientAltaMode ? "datos" : editDialogTab} onValueChange={clientAltaMode ? () => {} : setEditDialogTab} className="w-full">
            {!clientAltaMode ? (
            <TabsList
              className={`grid w-full ${editDialogTabColsClass} h-9 rounded-sm mb-2`}
            >
              <TabsTrigger value="datos" className="text-xs rounded-sm" data-testid="client-dialog-tab-datos">
                Datos del cliente
              </TabsTrigger>
              {editing?.id ? (
                <TabsTrigger value="constancia" className="text-xs rounded-sm" data-testid="client-dialog-tab-constancia">
                  <FileText className="w-3.5 h-3.5 mr-1" />
                  Constancia fiscal
                </TabsTrigger>
              ) : null}
              {editing?.id && showFiscalObligationsTab ? (
                <TabsTrigger value="obligaciones" className="text-xs rounded-sm" data-testid="client-dialog-tab-obligaciones">
                  <ListChecks className="w-3.5 h-3.5 mr-1" />
                  Obligaciones fiscales
                </TabsTrigger>
              ) : null}
              <TabsTrigger value="credenciales" className="text-xs rounded-sm" data-testid="edit-client-tab-credenciales">
                <KeyRound className="w-3.5 h-3.5 mr-1" />
                Certificados y FIEL
              </TabsTrigger>
            </TabsList>
            ) : null}

            {editing?.id ? (
              <TabsContent value="constancia" className="mt-0">
                <ClientCsfTabPanel
                  clientId={editing.id}
                  readOnly={!canCreate}
                  onApplyParsedFields={onApplyCsfParsedToForm}
                />
                <DialogFooter className="mt-4 flex flex-wrap gap-2 sm:justify-between">
                  <Button
                    type="button"
                    variant="link"
                    className="text-xs text-[#0F2D5C] px-0 h-auto"
                    onClick={() => setEditDialogTab("datos")}
                  >
                    ← Datos del cliente
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
                    Cerrar
                  </Button>
                </DialogFooter>
              </TabsContent>
            ) : null}

            {editing?.id && showFiscalObligationsTab ? (
              <TabsContent value="obligaciones" className="mt-0">
                <ClientFiscalObligationsTabPanel
                  clientId={editing.id}
                  readOnly={despachoFiscalReadOnly}
                />
                <DialogFooter className="mt-4 flex flex-wrap gap-2 sm:justify-between">
                  <Button
                    type="button"
                    variant="link"
                    className="text-xs text-[#0F2D5C] px-0 h-auto"
                    onClick={() => setEditDialogTab("constancia")}
                  >
                    ← Constancia fiscal
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
                    Cerrar
                  </Button>
                </DialogFooter>
              </TabsContent>
            ) : null}

            <TabsContent value="credenciales" className="mt-0">
              {clientAltaMode ? null : editing?.id ? (
                <>
                  <ClientCredentialsPanel clientId={editing.id} clientMeta={editing} />
                  <DialogFooter className="mt-4">
                    <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cerrar</Button>
                  </DialogFooter>
                </>
              ) : (
                <div className="space-y-4">
                  <Alert className="border-blue-200 bg-blue-50/80">
                    <KeyRound className="h-4 w-4" />
                    <AlertTitle className="text-sm">Primero registre al cliente</AlertTitle>
                    <AlertDescription className="text-xs space-y-2">
                      <p className="m-0">
                        La e.firma (FIEL) se asocia a un RFC ya guardado. Complete{" "}
                        <strong>RFC</strong> y <strong>razón social</strong> y pulse{" "}
                        <strong>Crear cliente y cargar FIEL</strong> (o créelo desde la pestaña Datos).
                      </p>
                      {!(form.rfc || "").trim() || !(form.razon_social || "").trim() ? (
                        <p className="m-0 text-amber-900">
                          Falta:{" "}
                          {[
                            !(form.rfc || "").trim() ? "RFC" : null,
                            !(form.razon_social || "").trim() ? "razón social" : null,
                          ]
                            .filter(Boolean)
                            .join(" y ")}
                          .
                        </p>
                      ) : (
                        <p className="m-0 text-emerald-900">
                          RFC y razón social listos — puede crear el cliente y cargar la FIEL aquí.
                        </p>
                      )}
                    </AlertDescription>
                  </Alert>
                  <DialogFooter className="flex flex-wrap gap-2 sm:justify-between px-0">
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-sm"
                      onClick={() => setEditDialogTab("datos")}
                      data-testid="client-credenciales-goto-datos"
                    >
                      Ir a datos del cliente
                    </Button>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        disabled={busy || !(form.rfc || "").trim() || !(form.razon_social || "").trim()}
                        className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                        data-testid="client-create-and-fiel-btn"
                        onClick={() => persistClient({ openCredentialsAfterCreate: true })}
                      >
                        {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1.5" />}
                        Crear cliente y cargar FIEL
                      </Button>
                    </div>
                  </DialogFooter>
                </div>
              )}
            </TabsContent>

            <TabsContent value="datos" className="mt-0 space-y-3">
          {(() => {
            const datosInner = (
              <>
          {!editing?.id && canCreate ? (
            <div className="space-y-3" data-testid="client-dialog-capture-method">
              <p className="text-[11px] text-slate-600 m-0 leading-snug">
                Complete los <strong>datos fiscales del contribuyente</strong>. Elija si los captura en el formulario
                o los importa desde la constancia del SAT (PDF).
              </p>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Método de captura de datos">
                <Button
                  type="button"
                  variant={dialogTab === "manual" ? "default" : "outline"}
                  size="sm"
                  className={
                    dialogTab === "manual"
                      ? "rounded-sm h-8 bg-[#0F2D5C] hover:bg-[#0a2248]"
                      : "rounded-sm h-8"
                  }
                  onClick={() => setDialogTab("manual")}
                  data-testid="client-dialog-method-manual"
                >
                  Formulario
                </Button>
                <Button
                  type="button"
                  variant={dialogTab === "csf" ? "default" : "outline"}
                  size="sm"
                  className={
                    dialogTab === "csf"
                      ? "rounded-sm h-8 bg-[#0F2D5C] hover:bg-[#0a2248]"
                      : "rounded-sm h-8"
                  }
                  onClick={() => setDialogTab("csf")}
                  data-testid="client-dialog-method-csf"
                >
                  <FileText className="w-3.5 h-3.5 mr-1.5" />
                  Importar PDF (CSF)
                </Button>
              </div>
              {dialogTab === "csf" ? (
                <div className="space-y-3 rounded-sm border border-slate-200 bg-slate-50/80 p-3" data-testid="client-dialog-csf-panel">
                  <p className="text-[11px] text-slate-600 leading-snug m-0">
                    Suba el PDF de la <strong>Constancia de Situación Fiscal</strong> del portal SAT (texto
                    seleccionable). El sistema propondrá RFC, razón social, régimen y domicilio en el formulario de abajo
                    para revisar antes de crear.
                  </p>
                  <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-sm p-5 cursor-pointer hover:border-[#3DA873] hover:bg-emerald-50/30 transition-colors bg-white">
                    {csfBusy ? (
                      <Loader2 className="w-8 h-8 animate-spin text-[#0F2D5C]" />
                    ) : (
                      <Upload className="w-8 h-8 text-slate-400" />
                    )}
                    <span className="text-xs font-medium text-slate-700">
                      {csfBusy ? "Leyendo PDF…" : "Seleccionar PDF de constancia"}
                    </span>
                    <input
                      type="file"
                      accept=".pdf,application/pdf"
                      className="hidden"
                      disabled={csfBusy}
                      onChange={onCsfPdfSelected}
                      data-testid="client-csf-file-input"
                    />
                  </label>
                  {csfWarnings.length > 0 ? (
                    <ul className="text-[10px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 space-y-1 list-disc pl-4 m-0">
                      {csfWarnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
          <form onSubmit={submit} className="grid grid-cols-2 gap-4 mt-1">
            <div>
              <Label className="text-xs">RFC *</Label>
              <div className="flex gap-2 items-center mt-1">
                <Input
                  data-testid="client-rfc-input"
                  required
                  value={form.rfc}
                  onChange={(e) => {
                    const rfc = e.target.value.toUpperCase();
                    const next = { ...form, rfc };
                    next.tipo_persona = inferTipoPersona(next);
                    if (isPersonaMoral(next)) {
                      next.curp = "";
                      next.contactos_pm = contactosPmFromClient(next);
                    } else {
                      next.representante_legal = "";
                      next.cargo_representante = "";
                      next.contactos_pm = emptyContactosPm();
                    }
                    setForm(next);
                  }}
                  className="rounded-sm font-mono flex-1"
                />
                <IdentityValidateButton rfc={form.rfc} />
              </div>
            </div>
            <div>
              <Label className="text-xs">
                {shouldShowClientCurp(form) ? "Nombre completo *" : "Razón social *"}
              </Label>
              <Input
                data-testid="client-name-input"
                required
                value={form.razon_social}
                onChange={(e) => setForm((prev) => mergeRazonSocialIntoForm(prev, e.target.value))}
                className="rounded-sm mt-1"
                placeholder={
                  shouldShowClientCurp(form)
                    ? "Nombre(s) y apellidos como en la CSF"
                    : "Denominación o razón social"
                }
              />
            </div>
            {shouldShowClientCurp(form) ? (
              <div className="col-span-2" data-testid="client-curp-field">
                <Label className="text-xs">CURP *</Label>
                <Input
                  data-testid="client-curp-input"
                  value={form.curp || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      curp: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 18),
                      tipo_persona: "fisica",
                    })
                  }
                  className="rounded-sm font-mono mt-1"
                  placeholder="18 caracteres — persona física"
                  maxLength={18}
                  autoComplete="off"
                />
                <p className="text-[10px] text-muted-foreground m-0 mt-1">
                  Obligatorio para persona física (RFC de 13 caracteres). Se rellena al subir la constancia.
                </p>
              </div>
            ) : null}
            <div>
              <Label className="text-xs">Régimen fiscal</Label>
              <Select
                value={normalizeRegimenFiscalForSelect(form.regimen_fiscal) || undefined}
                onValueChange={(v) => {
                  const next = { ...form, regimen_fiscal: v };
                  const tmp = { ...next, regimen_fiscal: v };
                  next.tipo_persona = inferTipoPersona(tmp);
                  if (!isPersonaMoral(tmp)) {
                    next.representante_legal = "";
                    next.cargo_representante = "";
                    next.contactos_pm = emptyContactosPm();
                  } else if (String(tmp.rfc || "").replace(/\s/g, "").length === 12) {
                    next.curp = "";
                  }
                  setForm(next);
                }}
              >
                <SelectTrigger className="rounded-sm mt-1" data-testid="client-regimen-select">
                  <SelectValue placeholder="Selecciona un régimen del catálogo SAT" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {REGIMENES_SAT.map((r) => (
                    <SelectItem key={r.c} value={regimenSatSelectValue(r)}>
                      <span className="font-mono text-xs mr-2">{r.c}</span>
                      <span>{r.n}</span>
                      <span className="ml-2 text-[10px] text-slate-500">
                        {r.pf && r.pm ? "(PF + PM)" : r.pf ? "(PF)" : r.pm ? "(PM)" : ""}
                      </span>
                    </SelectItem>
                  ))}
                  {(() => {
                    const raw = String(form.regimen_fiscal || "").trim();
                    const norm = normalizeRegimenFiscalForSelect(raw);
                    const inCatalog = REGIMENES_SAT.some((r) => regimenSatSelectValue(r) === norm);
                    if (!raw || inCatalog) return null;
                    return (
                      <SelectItem key="legacy-regimen" value={raw}>
                        {raw}
                      </SelectItem>
                    );
                  })()}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Código postal</Label>
              <Input value={form.codigo_postal} onChange={(e)=>setForm({...form, codigo_postal: e.target.value})} className="rounded-sm font-mono mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Dirección</Label>
              <Input value={form.direccion} onChange={(e)=>setForm({...form, direccion: e.target.value})} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs">Teléfono</Label>
              <Input value={form.telefono} onChange={(e)=>setForm({...form, telefono: e.target.value})} className="rounded-sm font-mono mt-1" />
            </div>

            <ClientContactosSection form={form} setForm={setForm} />

            <div className="col-span-2 rounded-md border border-slate-200 bg-slate-50/80 p-3 space-y-3">
              <p className="text-xs font-medium text-[#0F2D5C] m-0">Datos laborales y REPSE (opcional)</p>
              <p className="text-[11px] text-muted-foreground m-0">
                Se usarán al generar contratos y contratos REPSE. Los <strong>registros patronales</strong> y la{" "}
                <strong>e.firma (FIEL)</strong>
                {clientAltaMode
                  ? " se configuran en la sección 2 del acordeón (opcional tras crear la empresa)."
                  : (
                    <>
                      {" se configuran en la pestaña "}
                      <strong>Certificados y FIEL</strong>
                      {" (disponible al editar el cliente o tras crearlo)."}
                    </>
                  )}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Tipo de persona</Label>
                  <Select
                    value={form.tipo_persona || inferTipoPersona(form) || "moral"}
                    onValueChange={(v) => {
                      const next = { ...form, tipo_persona: v };
                      if (v === "fisica") {
                        next.representante_legal = "";
                        next.cargo_representante = "";
                        next.contactos_pm = emptyContactosPm();
                      } else if (String(form.rfc || "").replace(/\s/g, "").length === 12) {
                        next.curp = "";
                        next.contactos_pm = contactosPmFromClient(next);
                      } else {
                        // No borrar CURP si el RFC es de persona física (13).
                        next.contactos_pm = contactosPmFromClient(next);
                      }
                      setForm(next);
                    }}
                  >
                    <SelectTrigger className="rounded-sm mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="moral">Persona moral</SelectItem>
                      <SelectItem value="fisica">Persona física</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Actividad económica</Label>
                  <Input
                    value={form.actividad_economica}
                    onChange={(e) => setForm({ ...form, actividad_economica: e.target.value })}
                    className="rounded-sm mt-1"
                    placeholder="Objeto social o actividad principal"
                  />
                </div>
                {isPersonaMoral(form) ? (
                  <div className="col-span-2">
                    <Label className="text-xs">Cargo del representante</Label>
                    <Input
                      value={form.cargo_representante}
                      onChange={(e) => setForm({ ...form, cargo_representante: e.target.value })}
                      className="rounded-sm mt-1"
                      placeholder="Administrador Único, Apoderado Legal…"
                    />
                  </div>
                ) : null}
                <div>
                  <Label className="text-xs">Municipio</Label>
                  <Input value={form.municipio} onChange={(e) => setForm({ ...form, municipio: e.target.value })} className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Estado</Label>
                  <Input value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })} className="rounded-sm mt-1" />
                </div>
              </div>
            </div>
            <ClientFechaInicioControlField form={form} setForm={setForm} disabled={busy} />
            {canShowSatAutoSync ? (
              <>
                <ClientFechaInicioDescargaField form={form} setForm={setForm} disabled={busy} />
                <ClientSatAutoSyncToggle
                  form={form}
                  setForm={setForm}
                  clientId={editing?.id || null}
                  disabled={busy}
                  onPersisted={(doc) => {
                    if (editing?.id && doc) {
                      setEditing((prev) => (prev ? { ...prev, ...doc } : prev));
                      setForm((prev) => ({ ...prev, ...doc }));
                    }
                    load();
                  }}
                />
              </>
            ) : null}
            <div className="col-span-2">
              <Label className="text-xs">Notas</Label>
              <Input value={form.notas} onChange={(e)=>setForm({...form, notas: e.target.value})} className="rounded-sm mt-1" />
            </div>
            {!editing?.id && !clientAltaMode ? (
              <div className="col-span-2 rounded-md border border-blue-200 bg-blue-50/80 p-3 text-[11px] text-blue-950 leading-relaxed">
                <KeyRound className="w-3.5 h-3.5 inline mr-1" />
                Tras pulsar <strong>Crear</strong>, el sistema abrirá la pestaña{" "}
                <strong>Certificados y FIEL</strong> para cargar la e.firma (FIEL), certificados IMSS/CSD y registros patronales.
              </div>
            ) : null}
            {!editing?.id ? (
              repoServerManaged ? (
                <div
                  className="col-span-2 rounded-sm border border-emerald-200 bg-emerald-50/60 p-3 space-y-1"
                  data-testid="client-tax-repo-server-managed"
                >
                  <Label className="text-xs font-semibold text-[#0F2D5C]">
                    Exportación a disco (servidor)
                  </Label>
                  <p className="text-[11px] text-slate-600 leading-snug">
                    Los CFDI se guardan en la base de datos. Si el cliente solicita descargar archivos,
                    el sistema puede usar <strong>BNT.TaxRepository</strong> en el disco del servidor.
                  </p>
                </div>
              ) : (
              <div className="col-span-2 rounded-sm border border-slate-200 bg-slate-50 p-3 space-y-2">
                <Label className="text-xs font-semibold text-[#0F2D5C]">
                  Carpeta de exportación BNT.TaxRepository <span className="font-normal text-slate-500">(opcional)</span>
                </Label>
                <p className="text-[11px] text-slate-600 leading-snug">
                  Los CFDI se almacenan en la base de datos (nube). Configure esta carpeta <strong>solo</strong> si desea
                  exportar archivos al disco del equipo donde corre el backend (instalable .exe o servidor local).
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-sm h-9 border-[#0F2D5C] text-[#0F2D5C]"
                    disabled={taxRepoBusy}
                    onClick={onPickTaxRepository}
                    data-testid="client-tax-repo-pick-btn"
                  >
                    {taxRepoBusy ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <FolderOpen className="w-4 h-4 mr-1.5" />
                    )}
                    Seleccionar carpeta
                  </Button>
                  <Input
                    readOnly
                    value={taxRepoPath}
                    placeholder="Opcional — solo para exportación local…"
                    className="rounded-sm font-mono text-xs flex-1 min-w-[200px]"
                    data-testid="client-tax-repo-path"
                  />
                </div>
              </div>
              )
            ) : null}
            {editing && !repoServerManaged ? (
              <div className="col-span-2 rounded-sm border border-slate-200 bg-slate-50 p-3 space-y-2">
                <Label className="text-xs font-semibold text-[#0F2D5C]">
                  Repositorio local BNT.TaxRepository
                </Label>
                <p className="text-[11px] text-slate-600 leading-snug">
                  Si cambió de equipo o la carpeta ya no existe, genere de nuevo la estructura{" "}
                  <strong>XML_Descargados</strong> y <strong>Credenciales_IMSS</strong> para el RFC del cliente.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-sm h-9 border-[#0F2D5C] text-[#0F2D5C]"
                  disabled={repoEnsureBusyId === editing.id}
                  onClick={() => onEnsureLocalRepository(editing)}
                  data-testid="client-ensure-repo-btn"
                >
                  {repoEnsureBusyId === editing.id ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <FolderOpen className="w-4 h-4 mr-1.5" />
                  )}
                  Generar / Verificar Repositorio Local
                </Button>
              </div>
            ) : null}
            <DialogFooter className="col-span-2 mt-2 flex flex-wrap items-center gap-2 sm:justify-between">
              {editing && !clientAltaMode ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="link"
                    className="text-xs text-[#0F2D5C] px-0 h-auto"
                    onClick={() => setEditDialogTab("constancia")}
                  >
                    <FileText className="w-3.5 h-3.5 mr-1" />
                    Constancia fiscal →
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    className="text-xs text-[#0F2D5C] px-0 h-auto"
                    onClick={() => setEditDialogTab("credenciales")}
                  >
                    <KeyRound className="w-3.5 h-3.5 mr-1" />
                    FIEL y registros patronales →
                  </Button>
                  {canDelete ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-sm h-8 border-red-200 text-red-700 hover:bg-red-50"
                    onClick={() => {
                      if (editing) remove(editing);
                    }}
                    data-testid="delete-client-dialog-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    Eliminar cliente
                  </Button>
                  ) : null}
                </div>
              ) : (
                <span />
              )}
              <div className="flex gap-2 ml-auto">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
                  Cancelar
                </Button>
                <Button
                  data-testid="save-client-btn"
                  type="submit"
                  disabled={busy}
                  className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                >
                  {clientAltaMode && !editing?.id ? "Crear empresa" : editing?.id ? "Actualizar" : "Crear"}
                </Button>
              </div>
            </DialogFooter>
          </form>
              </>
            );
            if (!clientAltaMode) return datosInner;
            return (
              <>
                {editing?.id ? (
                  <Alert className="border-emerald-200 bg-emerald-50/90 rounded-sm mb-3" data-testid="client-alta-created-banner">
                    <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                    <AlertTitle className="text-sm text-emerald-950">Empresa registrada</AlertTitle>
                    <AlertDescription className="text-xs text-emerald-900">
                      <span className="font-mono font-semibold">{form.rfc}</span>
                      {" — "}
                      {form.razon_social}. Puede cargar la e.firma en la sección 2 o cerrar y hacerlo después.
                    </AlertDescription>
                  </Alert>
                ) : null}
                <Accordion
                  type="multiple"
                  value={altaAccordionOpen}
                  onValueChange={setAltaAccordionOpen}
                  className="w-full rounded-sm border border-slate-200 px-3"
                  data-testid="client-alta-accordion"
                >
                  <AccordionItem value="datos" className="border-slate-200" data-testid="client-alta-accordion-datos">
                    <AccordionTrigger
                      className="text-xs font-semibold text-[#0F2D5C] py-3 hover:no-underline"
                      data-testid="client-dialog-tab-datos"
                    >
                      1. Datos fiscales del contribuyente
                    </AccordionTrigger>
                    <AccordionContent className="pb-4 pt-0 space-y-3">{datosInner}</AccordionContent>
                  </AccordionItem>
                  <AccordionItem
                    value="credenciales"
                    className="border-slate-200 border-b-0"
                    data-testid="client-alta-accordion-credenciales"
                    disabled={!editing?.id}
                  >
                    <AccordionTrigger className="text-xs font-semibold text-[#0F2D5C] py-3 hover:no-underline">
                      2. e.firma y certificados (opcional)
                    </AccordionTrigger>
                    <AccordionContent className="pb-4 pt-0">
                      {editing?.id ? (
                        <>
                          <ClientCredentialsPanel clientId={editing.id} clientMeta={editing} />
                          <DialogFooter className="mt-4 px-0 flex flex-wrap gap-2 sm:justify-end">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setOpen(false)}
                              className="rounded-sm"
                              data-testid="client-alta-skip-credenciales-btn"
                            >
                              Omitir por ahora
                            </Button>
                            <Button type="button" onClick={() => setOpen(false)} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                              Cerrar
                            </Button>
                          </DialogFooter>
                        </>
                      ) : (
                        <p className="text-xs text-slate-600 m-0 py-2">
                          Complete el paso 1 y pulse <strong>Crear empresa</strong> para habilitar la carga de e.firma,
                          IMSS y registros patronales.
                        </p>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </>
            );
          })()}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
